import React from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiChevronDown, FiCheck, FiDollarSign, FiHome, FiPieChart, FiTrendingUp, FiShield, FiCalendar } from 'react-icons/fi';
import styled from 'styled-components';

// Styled component for image overlay
const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 20px;
  border-radius: 8px;
  transform: translateY(100%);
  opacity: 0;
  transition: transform 0.3s ease, opacity 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow-y: auto;

  h4 {
    margin: 0 0 10px;
    font-size: 18px;
    color: var(--theme-color);
  }

  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin-bottom: 8px;
    position: relative;
    padding-left: 20px;
  }

  li:before {
    content: "•";
    color: var(--theme-color);
    position: absolute;
    left: 0;
    font-weight: bold;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  margin: 20px 0;

  &:hover ${ImageOverlay} {
    transform: translateY(0);
    opacity: 1;
  }

  .content-image {
    display: block;
    width: 100%;
    height: auto;
  }
`;

export default function RealEstateMortgageConsulting() {
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('diversification');
    const [expandedSections, setExpandedSections] = useState({});

    useEffect(() => {
        setTimeout(() => {
            setIsLoading(false);
        }, 2000);
    }, []);

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const sectionVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    return (
        <div className="real-estate-page">
            <style jsx>{`
                :root {
                    --theme-color: #f7bf68;
                }
                .real-estate-page {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    color: #333;
                    line-height: 1.6;
                }

                /* Page Title Styles */
                .page-title .content-box h1 {
                    color: #fff;
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin-bottom: 15px;
                }

                /* Main Content Styles */
                .real-estate-content {
                    padding: 60px 0;
                    background-color: #f9fafc;
                }

                .content-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 20px;
                }

                /* Tab Navigation */
                .tab-nav {
                    display: flex;
                    margin-bottom: 30px;
                    border-bottom: 1px solid #e1e5eb;
                    flex-wrap: wrap;
                }

                .tab-btn {
                    padding: 12px 25px;
                    margin-right: 10px;
                    background: none;
                    border: none;
                    border-bottom: 3px solid transparent;
                    font-size: 16px;
                    font-weight: 600;
                    color: #6b7280;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                }

                .tab-btn:hover {
                    color: var(--theme-color);
                }

                .tab-btn.active {
                    color: var(--theme-color);
                    border-bottom-color: var(--theme-color);
                }

                .tab-icon {
                    margin-right: 8px;
                    font-size: 18px;
                }

                /* Content Sections */
                .content-section {
                    background: white;
                    padding: 30px;
                    border-radius: 10px;
                    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
                    margin-bottom: 30px;
                }

                .section-header {
                    margin-bottom: 25px;
                    text-align: center;
                }

                .section-header h2 {
                    color: var(--theme-color);
                    font-size: 28px;
                    margin-bottom: 10px;
                    font-weight: 700;
                }

                .section-header p.subtitle {
                    color: #6b7280;
                    font-size: 18px;
                    max-width: 800px;
                    margin: 0 auto;
                }

                /* Portfolio Diversification */
                .portfolio-pie {
                    width: 300px;
                    height: 300px;
                    margin: 30px auto;
                    border-radius: 50%;
                    position: relative;
                }

                .portfolio-legend {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    gap: 15px;
                    margin-top: 30px;
                }

                .legend-item {
                    display: flex;
                    align-items: center;
                    padding: 8px 15px;
                    background: #f8fafc;
                    border-radius: 20px;
                    font-size: 14px;
                }

                .legend-color {
                    width: 15px;
                    height: 15px;
                    border-radius: 3px;
                    margin-right: 8px;
                }

                /* Entity Structure */
                .entity-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 20px 0;
                    font-size: 15px;
                }

                .entity-table th {
                    background-color: var(--theme-color);
                    color: white;
                    text-align: left;
                    padding: 12px 15px;
                    font-weight: 600;
                }

                .entity-table td {
                    padding: 12px 15px;
                    border-bottom: 1px solid #e5e7eb;
                }

                .entity-table tr:nth-child(even) {
                    background-color: #f9fafb;
                }

                .entity-table tr:hover {
                    background-color: #f3f4f6;
                }

                /* Flow Guide */
                .flow-guide {
                    background: #f8fafc;
                    padding: 25px;
                    border-radius: 8px;
                    margin: 25px 0;
                }

                .flow-step {
                    margin-bottom: 20px;
                    padding-left: 30px;
                    position: relative;
                }

                .flow-step:before {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 5px;
                    width: 20px;
                    height: 20px;
                    background-color: var(--theme-color);
                    border-radius: 50%;
                }

                .flow-step:after {
                    content: '';
                    position: absolute;
                    left: 9px;
                    top: 25px;
                    bottom: -25px;
                    width: 2px;
                    background-color: #e5e7eb;
                }

                .flow-step:last-child:after {
                    display: none;
                }

                .flow-question {
                    font-weight: 600;
                    color: #111827;
                    margin-bottom: 8px;
                }

                .flow-answer {
                    padding-left: 15px;
                    color: #4b5563;
                }

                /* Tax Section */
                .tax-example {
                    background: #fff8ec;
                    border-left: 4px solid var(--theme-color);
                    padding: 20px;
                    border-radius: 0 8px 8px 0;
                    margin: 20px 0;
                }

                .tax-example h4 {
                    color: var(--theme-color);
                    margin-top: 0;
                }

                /* Mortgage Rate Section */
                .rate-factors {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 20px;
                    margin: 25px 0;
                }

                .factor-card {
                    background: white;
                    border-radius: 8px;
                    padding: 20px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
                    border-top: 3px solid var(--theme-color);
                }

                .factor-card h4 {
                    color: var(--theme-color);
                    margin-top: 0;
                    display: flex;
                    align-items: center;
                }

                .factor-icon {
                    margin-right: 10px;
                    color: var(--theme-color);
                }

                /* Property Analyzer */
                .formula-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                    gap: 20px;
                    margin: 25px 0;
                }

                .formula-card {
                    background: white;
                    border-radius: 8px;
                    padding: 20px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
                    border-left: 3px solid var(--theme-color);
                }

                .formula-card h4 {
                    margin-top: 0;
                    color: var(--theme-color);
                }

                .formula {
                    font-family: 'Courier New', Courier, monospace;
                    background: #f8fafc;
                    padding: 10px;
                    border-radius: 4px;
                    margin: 10px 0;
                    overflow-x: auto;
                }

                /* Rental Snapshot */
                .snapshot-container {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 25px;
                    margin: 30px 0;
                }

                .property-snapshot {
                    background: white;
                    border-radius: 8px;
                    padding: 20px;
                    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
                }

                .property-snapshot h3 {
                    color: var(--theme-color);
                    border-bottom: 1px solid #e5e7eb;
                    padding-bottom: 10px;
                    margin-top: 0;
                }

                .snapshot-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 8px 0;
                    border-bottom: 1px dashed #e5e7eb;
                }

                .snapshot-label {
                    font-weight: 500;
                    color: #4b5563;
                }

                .snapshot-value {
                    font-weight: 600;
                }

                /* Goal Setter */
                .goal-setter {
                    background: #f8fafc;
                    padding: 30px;
                    border-radius: 10px;
                    margin: 30px 0;
                }

                .goal-inputs {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 20px;
                    margin-bottom: 25px;
                }

                .input-group {
                    margin-bottom: 15px;
                }

                .input-group label {
                    display: block;
                    margin-bottom: 5px;
                    font-weight: 500;
                    color: #4b5563;
                }

                .input-group input {
                    width: 100%;
                    padding: 10px;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    font-size: 16px;
                }

                .goal-results {
                    background: white;
                    padding: 20px;
                    border-radius: 8px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
                }

                .result-item {
                    margin-bottom: 15px;
                }

                .result-label {
                    font-weight: 600;
                    color: var(--theme-color);
                }

                /* Tax Strategy */
                .tax-benefits {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                    gap: 20px;
                    margin: 25px 0;
                }

                .benefit-card {
                    background: white;
                    border-radius: 8px;
                    padding: 20px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
                    border-top: 3px solid var(--theme-color);
                }

                .benefit-card h4 {
                    color: var(--theme-color);
                    margin-top: 0;
                    display: flex;
                    align-items: center;
                }

                .benefit-icon {
                    margin-right: 10px;
                    color: var(--theme-color);
                }

                /* Image Styling */
                .content-image {
                    max-width: 50%;
                    height: auto;
                    border-radius: 8px;
                    margin: 20px 0;
                    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
                }

                /* Responsive Adjustments */
                @media (max-width: 768px) {
                    .tab-nav {
                        flex-direction: column;
                    }
                    
                    .tab-btn {
                        margin-bottom: 10px;
                        text-align: left;
                        justify-content: flex-start;
                    }
                    
                    .portfolio-pie {
                        width: 250px;
                        height: 250px;
                    }
                    
                    .content-section {
                        padding: 20px;
                    }
                    
                    .goal-inputs {
                        grid-template-columns: 1fr;
                    }

                    .content-image {
                        max-width: 100%;
                    }
                }

                /* Expandable Sections */
                .expandable-section {
                    margin-bottom: 20px;
                }

                .section-header-toggle {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    cursor: pointer;
                    padding: 15px;
                    background: #f8fafc;
                    border-radius: 8px;
                    transition: all 0.3s ease;
                }

                .section-header-toggle:hover {
                    background: #f1f5f9;
                }

                .section-header-toggle h3 {
                    margin: 0;
                    color: var(--theme-color);
                    display: flex;
                    align-items: center;
                }

                .section-icon {
                    margin-right: 10px;
                }

                .section-content {
                    padding: 20px;
                    background: white;
                    border-radius: 0 0 8px 8px;
                    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
                    margin-top: -5px;
                }

                .toggle-icon {
                    transition: transform 0.3s ease;
                }

                .toggle-icon.rotated {
                    transform: rotate(180deg);
                }

                /* Styled List */
                .styled-list {
                    list-style-type: none;
                    padding-left: 0;
                }

                .styled-list li {
                    position: relative;
                    padding-left: 25px;
                    margin-bottom: 10px;
                    line-height: 1.6;
                }

                .styled-list li:before {
                    content: "•";
                    color: var(--theme-color);
                    font-weight: bold;
                    position: absolute;
                    left: 0;
                }
            `}</style>

            <div className="boxed_wrapper ltr">
                {isLoading && <div className="loader-wrap">
                    <div className="preloader">
                        <div className="preloader-close">close</div>
                        <div id="handle-preloader" className="handle-preloader">
                            <div className="animation-preloader">
                                <div className="txt-loading">
                                    <img src="assets/images/animation_logo.gif" alt="img" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>}

                <Header />

                {/* page-title */}
                <section className="page-title">
                    <div
                        className="bg-layer"
                        style={{
                            backgroundImage: `url("/assets/images/background/page-title.jpg")`,
                        }}
                    ></div>
                    <div className="line-grid-wrap">
                        <span className="line-grid-1"></span>
                        <span className="line-grid-2"></span>
                        <span className="line-grid-3"></span>
                        <span className="line-grid-4"></span>
                        <span className="line-grid-5"></span>
                    </div>
                    <div className="auto-container">
                        <div className="content-box">
                            <h1 style={{ textAlign: "left" }}>Real Estate & Mortgage Consulting Services</h1>
                            <ul className="bread-crumb clearfix">
                                <li>
                                    <Link to="/">Home</Link>
                                </li>
                                <li>Real Estate & Mortgage Consulting Services</li>
                            </ul>
                        </div>
                    </div>
                </section>
                {/* Page Title End */}

                {/* Main Content Section */}
                <section className="real-estate-content">
                    <div className="content-container">
                        {/* Tab Navigation */}
                        <motion.div 
                            className="tab-nav"
                            initial="hidden"
                            animate="visible"
                            variants={fadeIn}
                        >
                            <button 
                                className={`tab-btn ${activeTab === 'diversification' ? 'active' : ''}`}
                                onClick={() => setActiveTab('diversification')}
                            >
                                <FiPieChart className="tab-icon" />
                                Portfolio Diversification
                            </button>
                            <button 
                                className={`tab-btn ${activeTab === 'entities' ? 'active' : ''}`}
                                onClick={() => setActiveTab('entities')}
                            >
                                <FiShield className="tab-icon" />
                                Entity Structures
                            </button>
                            <button 
                                className={`tab-btn ${activeTab === 'tax' ? 'active' : ''}`}
                                onClick={() => setActiveTab('tax')}
                            >
                                <FiDollarSign className="tab-icon" />
                                Tax Strategies
                            </button>
                            <button 
                                className={`tab-btn ${activeTab === 'mortgage' ? 'active' : ''}`}
                                onClick={() => setActiveTab('mortgage')}
                            >
                                <FiHome className="tab-icon" />
                                Mortgage Rates
                            </button>
                            <button 
                                className={`tab-btn ${activeTab === 'analysis' ? 'active' : ''}`}
                                onClick={() => setActiveTab('analysis')}
                            >
                                <FiTrendingUp className="tab-icon" />
                                Property Analysis
                            </button>
                        </motion.div>

                        {/* Portfolio Diversification Tab */}
                        {activeTab === 'diversification' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Portfolio Diversification Blueprint</h2>
                                    <p className="subtitle">Build a resilient real estate portfolio using a mix of rental types, markets, and cash flow strategies</p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="expandable-section">
                                    <div 
                                        className="section-header-toggle"
                                        onClick={() => toggleSection('whyDiversify')}
                                    >
                                        <h3><FiPieChart className="section-icon" /> Why Diversification Matters</h3>
                                        <FiChevronDown className={`toggle-icon ${expandedSections['whyDiversify'] ? 'rotated' : ''}`} />
                                    </div>
                                    {expandedSections['whyDiversify'] && (
                                        <div className="section-content">
                                            <ul className="styled-list">
                                                <li>Reduces vacancy risk by spreading across different property types</li>
                                                <li>Balances appreciation potential with steady cash flow</li>
                                                <li>Protects against market downturns in specific sectors</li>
                                                <li>Mitigates regulatory changes that might affect one property type</li>
                                            </ul>
                                        </div>
                                    )}
                                </motion.div>

                                <motion.div variants={fadeIn} className="portfolio-pie" style={{
                                  background: 'conic-gradient(#4CAF50 0% 25%, #FF9800 25% 40%, #2196F3 40% 55%, #9C27B0 55% 70%, #F44336 70% 100%)',
                                  width: '300px',
                                  height: '300px',
                                  margin: '30px auto',
                                  borderRadius: '50%',
                                  position: 'relative',
                                }}></motion.div>

                                <motion.div variants={fadeIn} className="portfolio-legend">
                                  <div className="legend-item">
                                    <div className="legend-color" style={{ backgroundColor: '#4CAF50' }}></div>
                                    <span>Long-Term Rental (25%)</span>
                                  </div>
                                  <div className="legend-item">
                                    <div className="legend-color" style={{ backgroundColor: '#FF9800' }}></div>
                                    <span>Short-Term Rental (15%)</span>
                                  </div>
                                  <div className="legend-item">
                                    <div className="legend-color" style={{ backgroundColor: '#2196F3' }}></div>
                                    <span>Commercial (10%)</span>
                                  </div>
                                  <div className="legend-item">
                                    <div className="legend-color" style={{ backgroundColor: '#9C27B0' }}></div>
                                    <span>REITs (10%)</span>
                                  </div>
                                  <div className="legend-item">
                                    <div className="legend-color" style={{ backgroundColor: '#F44336' }}></div>
                                    <span>Raw Land (5%)</span>
                                  </div>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/RMS1.jpg" 
                                            alt="Real estate portfolio diversification" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Diversification Benefits</h4>
                                            <ul>
                                                <li>Reduces vacancy risks.</li>
                                                <li>Balances cash flow and growth.</li>
                                                <li>Protects against market dips.</li>
                                                <li>Mitigates regulatory impacts.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="expandable-section">
                                    <div 
                                        className="section-header-toggle"
                                        onClick={() => toggleSection('samplePortfolios')}
                                    >
                                        <h3><FiHome className="section-icon" /> Sample Portfolio Scenarios</h3>
                                        <FiChevronDown className={`toggle-icon ${expandedSections['samplePortfolios'] ? 'rotated' : ''}`} />
                                    </div>
                                    {expandedSections['samplePortfolios'] && (
                                        <div className="section-content">
                                            <h4>Scenario 1: $2,500/month goal</h4>
                                            <ul className="styled-list">
                                                <li>1 Short-Term Rental = $1,500</li>
                                                <li>1 Long-Term Rental = $800</li>
                                                <li>1 Out-of-State Duplex = $200</li>
                                                <li>Startup Capital Needed: ~$90K</li>
                                            </ul>
                                            
                                            <h4>Scenario 2: $7,500/month goal</h4>
                                            <ul className="styled-list">
                                                <li>2 STRs + 2 LTRs + 1 Commercial suite</li>
                                                <li>Startup Capital Needed: ~$250K</li>
                                                <li>Balanced for cash flow and tax benefits</li>
                                            </ul>
                                        </div>
                                    )}
                                </motion.div>

                                <motion.div variants={fadeIn} className="expandable-section">
                                    <div 
                                        className="section-header-toggle"
                                        onClick={() => toggleSection('diversificationTips')}
                                    >
                                        <h3><FiCheck className="section-icon" /> Ravline Diversification Tips</h3>
                                        <FiChevronDown className={`toggle-icon ${expandedSections['diversificationTips'] ? 'rotated' : ''}`} />
                                    </div>
                                    {expandedSections['diversificationTips'] && (
                                        <div className="section-content">
                                            <ul className="styled-list">
                                                <li>Allocate 50% to Cash Flow Properties</li>
                                                <li>30% to Appreciation Plays</li>
                                                <li>20% to High-Yield (STR/Commercial)</li>
                                                <li>Rebalance every 12-18 months</li>
                                                <li>Consider geographic diversification</li>
                                                <li>Mix property types (residential, commercial, land)</li>
                                            </ul>
                                        </div>
                                    )}
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Entity Structures Tab */}
                        {activeTab === 'entities' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Entity & Ownership Structure Guide</h2>
                                    <p className="subtitle">Choose the most effective legal structures to own and protect your real estate assets</p>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <h3>Entity Comparison Table</h3>
                                    <table className="entity-table">
                                        <thead>
                                            <tr>
                                                <th>Method</th>
                                                <th>Pros</th>
                                                <th>Cons</th>
                                                <th>Best For</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>LLC</td>
                                                <td>Liability protection, separate assets</td>
                                                <td>Ongoing cost, annual filings</td>
                                                <td>Rentals, STRs, partners</td>
                                            </tr>
                                            <tr>
                                                <td>Land Trust</td>
                                                <td>Privacy, ease of transfer</td>
                                                <td>Less liability protection</td>
                                                <td>Holding property discreetly</td>
                                            </tr>
                                            <tr>
                                                <td>SDIRA</td>
                                                <td>Tax-deferred or Roth growth</td>
                                                <td>Strict rules, limited use</td>
                                                <td>Retirement investing</td>
                                            </tr>
                                            <tr>
                                                <td>Personal</td>
                                                <td>Simplicity, low cost</td>
                                                <td>Full liability exposure</td>
                                                <td>Primary residence</td>
                                            </tr>
                                            <tr>
                                                <td>Irrevocable Trust</td>
                                                <td>Estate planning, asset protection</td>
                                                <td>Hard to modify, cost</td>
                                                <td>Generational holding</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/RMS2.png" 
                                            alt="Entity structure comparison" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Entity Structure Highlights</h4>
                                            <ul>
                                                <li>LLC offers liability protection.</li>
                                                <li>Land Trust ensures privacy.</li>
                                                <li>SDIRA supports tax-deferred growth.</li>
                                                <li>Personal use is simple but risky.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn} className="flow-guide">
                                    <h3>Ownership Structure Flow Guide</h3>
                                    <div className="flow-step">
                                        <div className="flow-question">1. Do you plan to rent or flip?</div>
                                        <div className="flow-answer">
                                            - Rent = Use LLC or Trust<br />
                                            - Flip = Use Corporation (S-Corp for tax efficiency)
                                        </div>
                                    </div>
                                    <div className="flow-step">
                                        <div className="flow-question">2. Are you using retirement funds?</div>
                                        <div className="flow-answer">
                                            - Yes = Use Self-Directed IRA (SDIRA) + Custodian
                                        </div>
                                    </div>
                                    <div className="flow-step">
                                        <div className="flow-question">3. Is this for personal cash flow or legacy?</div>
                                        <div className="flow-answer">
                                            - Legacy = Include in Irrevocable Trust<br />
                                            - Cash Flow = Use LLC + liability insurance
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Tax Strategies Tab */}
                        {activeTab === 'tax' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Tax Strategy & Passive Income</h2>
                                    <p className="subtitle">Leverage real estate for tax reduction and long-term passive income generation</p>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <h3>Section 121 Exclusion – Sell Your Home Tax-Free</h3>
                                    <p>The Section 121 Exclusion is an IRS tax benefit that lets homeowners <strong>exclude up to $250,000 in gains</strong> from the sale of their primary home — <strong>or $500,000 for married couples filing jointly.</strong></p>
                                    
                                    <div className="tax-example">
                                        <h4>Example:</h4>
                                        <p>Bought for: $300,000<br />
                                        Sold for: $600,000<br />
                                        Capital Gain: $300,000</p>
                                        <ul className="styled-list">
                                            <li><strong>If married:</strong> You can exclude the entire $300,000 gain from tax.</li>
                                            <li><strong>If single:</strong> You exclude $250,000, and only pay tax on $50,000.</li>
                                        </ul>
                                    </div>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <h3>Tax Advantages of Real Estate</h3>
                                    <div className="tax-benefits">
                                        <div className="benefit-card">
                                            <h4><FiDollarSign className="benefit-icon" /> Depreciation</h4>
                                            <p>Deduct property wear-and-tear (27.5 years for residential, 39 for commercial)</p>
                                        </div>
                                        <div className="benefit-card">
                                            <h4><FiDollarSign className="benefit-icon" /> Mortgage Interest Deduction</h4>
                                            <p>Reduces taxable income significantly on leveraged property</p>
                                        </div>
                                        <div className="benefit-card">
                                            <h4><FiDollarSign className="benefit-icon" /> 1031 Exchange</h4>
                                            <p>Swap properties without immediate tax burden</p>
                                        </div>
                                        <div className="benefit-card">
                                            <h4><FiDollarSign className="benefit-icon" /> Cost Segregation</h4>
                                            <p>Accelerate depreciation into 5, 7, or 15-year categories</p>
                                        </div>
                                        <div className="benefit-card">
                                            <h4><FiDollarSign className="benefit-icon" /> Passive Loss Offset</h4>
                                            <p>Offset passive gains or up to $25K of regular income (if qualified)</p>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/RMS3.jpg" 
                                            alt="Real estate tax benefits" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Tax Benefits</h4>
                                            <ul>
                                                <li>Depreciation reduces taxable income.</li>
                                                <li>Mortgage interest offers deductions.</li>
                                                <li>1031 Exchange defers taxes.</li>
                                                <li>Cost segregation speeds depreciation.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <h3>Building Passive Income with Real Estate</h3>
                                    <div className="tax-example">
                                        <h4>Example:</h4>
                                        <ul className="styled-list">
                                            <li>Purchase Price: $300,000</li>
                                            <li>Down Payment: $60,000 (20%)</li>
                                            <li>Monthly Rent: $2,400</li>
                                            <li>Expenses: $1,800</li>
                                            <li>Net Monthly Cash Flow: $600</li>
                                            <li>Net Annual Cash Flow: $7,200 (12% return on $60K invested)</li>
                                        </ul>
                                        <p>After 10 years, a $300K property could appreciate to $420K while generating $72K in passive income.</p>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Mortgage Rates Tab */}
                        {activeTab === 'mortgage' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Understanding Mortgage Rate Determinants in 2025</h2>
                                    <p className="subtitle">Navigate the lending markets with insights into what drives mortgage rates</p>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <h3>Economic Indicators That Influence Mortgage Rates</h3>
                                    <div className="rate-factors">
                                        <div className="factor-card">
                                            <h4><FiTrendingUp className="factor-icon" /> 10-Year Treasury Yield</h4>
                                            <p>A leading indicator for mortgage rate trends</p>
                                        </div>
                                        <div className="factor-card">
                                            <h4><FiTrendingUp className="factor-icon" /> Federal Reserve Policy</h4>
                                            <p>Impacts rates through the federal funds rate</p>
                                        </div>
                                        <div className="factor-card">
                                            <h4><FiTrendingUp className="factor-icon" /> Inflation Rate</h4>
                                            <p>Higher inflation tends to push interest rates upward</p>
                                        </div>
                                        <div className="factor-card">
                                            <h4><FiTrendingUp className="factor-icon" /> GDP Growth</h4>
                                            <p>Economic expansion leads to higher demand for credit</p>
                                        </div>
                                        <div className="factor-card">
                                            <h4><FiTrendingUp className="factor-icon" /> Employment Growth</h4>
                                            <p>Strength in labor market can signal upward pressure on rates</p>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/RMS4.jpg" 
                                            alt="Mortgage rate trends" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Rate Determinants</h4>
                                            <ul>
                                                <li>10-Year Treasury Yield leads trends.</li>
                                                <li>Fed policy affects rates.</li>
                                                <li>Inflation pushes rates up.</li>
                                                <li>GDP growth increases credit demand.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <h3>Personal Financial Factors</h3>
                                    <ul className="styled-list">
                                        <li><strong>Credit Score:</strong> A borrower's score greatly affects eligibility and rate</li>
                                        <li><strong>Loan-to-Value (LTV) Ratio:</strong> Higher down payments reduce LTV and often result in lower interest</li>
                                        <li><strong>Debt-to-Income Ratio (DTI):</strong> Lower ratios indicate financial stability</li>
                                        <li><strong>Loan Type:</strong> Fixed vs adjustable, 15-year vs 30-year terms all influence pricing</li>
                                        <li><strong>Property Type:</strong> Investment properties or vacation homes typically carry higher rates</li>
                                    </ul>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <h3>Current Rate Trends and the 2025 Outlook</h3>
                                    <p>As of 2025, mortgage rates are stabilizing near 6.9% for 30-year fixed loans. This reflects a balancing act between easing inflation and cautious optimism in economic growth.</p>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <h3>Best Practices for Borrowers in 2025</h3>
                                    <ul className="styled-list">
                                        <li>Monitor interest rate forecasts and treasury yields</li>
                                        <li>Improve your credit score before applying</li>
                                        <li>Compare loan products and lenders</li>
                                        <li>Lock in rates strategically when favorable</li>
                                        <li>Consider shorter terms or larger down payments for better terms</li>
                                    </ul>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Property Analysis Tab */}
                        {activeTab === 'analysis' && (
                            <motion.div 
                                className="content-section"
                                initial="hidden"
                                animate="visible"
                                variants={sectionVariants}
                            >
                                <motion.div variants={fadeIn} className="section-header">
                                    <h2>Investment Property Analyzer</h2>
                                    <p className="subtitle">Essential formulas and tools to evaluate real estate investment performance</p>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <h3>Key Formulas</h3>
                                    <div className="formula-grid">
                                        <div className="formula-card">
                                            <h4>Gross Monthly Rent</h4>
                                            <div className="formula">Monthly Rent x Number of Units</div>
                                        </div>
                                        <div className="formula-card">
                                            <h4>Annual Gross Rent</h4>
                                            <div className="formula">Gross Monthly Rent x 12</div>
                                        </div>
                                        <div className="formula-card">
                                            <h4>Effective Gross Income (EGI)</h4>
                                            <div className="formula">Annual Gross Rent x (1 - Vacancy Rate)</div>
                                        </div>
                                        <div className="formula-card">
                                            <h4>Net Operating Income (NOI)</h4>
                                            <div className="formula">EGI - Operating Expenses</div>
                                        </div>
                                        <div className="formula-card">
                                            <h4>Cap Rate (%)</h4>
                                            <div className="formula">NOI ÷ Purchase Price x 100</div>
                                        </div>
                                        <div className="formula-card">
                                            <h4>Cash-on-Cash Return (%)</h4>
                                            <div className="formula">Cash Flow Before Taxes ÷ Initial Cash Invested x 100</div>
                                        </div>
                                    </div>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <h3>Monthly Rental Revenue Snapshot</h3>
                                    <div className="snapshot-container">
                                        <div className="property-snapshot">
                                            <h3>Property 1</h3>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Gross Rent:</span>
                                                <span className="snapshot-value">$2,400</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Vacancy Loss:</span>
                                                <span className="snapshot-value">$120</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Taxes:</span>
                                                <span className="snapshot-value">$300</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Insurance:</span>
                                                <span className="snapshot-value">$150</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Net Cash Flow:</span>
                                                <span className="snapshot-value">$1,830</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Occupancy Rate:</span>
                                                <span className="snapshot-value">95%</span>
                                            </div>
                                        </div>

                                        <div className="property-snapshot">
                                            <h3>Property 2</h3>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Gross Rent:</span>
                                                <span className="snapshot-value">$3,200</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Vacancy Loss:</span>
                                                <span className="snapshot-value">$0</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Taxes:</span>
                                                <span className="snapshot-value">$400</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Insurance:</span>
                                                <span className="snapshot-value">$180</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Net Cash Flow:</span>
                                                <span className="snapshot-value">$2,620</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Occupancy Rate:</span>
                                                <span className="snapshot-value">100%</span>
                                            </div>
                                        </div>

                                        <div className="property-snapshot">
                                            <h3>Property 3</h3>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Gross Rent:</span>
                                                <span className="snapshot-value">$1,800</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Vacancy Loss:</span>
                                                <span className="snapshot-value">$360</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Taxes:</span>
                                                <span className="snapshot-value">$250</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Insurance:</span>
                                                <span className="snapshot-value">$120</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Net Cash Flow:</span>
                                                <span className="snapshot-value">$1,070</span>
                                            </div>
                                            <div className="snapshot-row">
                                                <span className="snapshot-label">Occupancy Rate:</span>
                                                <span className="snapshot-value">80%</span>
                                            </div>
                                        </div>
                                    </div>
                                    <ImageWrapper>
                                        <img 
                                            src="/assets/images/RMS5.jpg" 
                                            alt="Rental revenue snapshot" 
                                            className="content-image" 
                                            width="600" 
                                            height="400" 
                                        />
                                        <ImageOverlay>
                                            <h4>Rental Insights</h4>
                                            <ul>
                                                <li>Property 1 nets $1,830 at 95% occupancy.</li>
                                                <li>Property 2 nets $2,620 at 100% occupancy.</li>
                                                <li>Property 3 nets $1,070 at 80% occupancy.</li>
                                                <li>Average cash flow varies by occupancy.</li>
                                            </ul>
                                        </ImageOverlay>
                                    </ImageWrapper>
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <h3>Real Estate Goal Setter</h3>
                                    <div className="goal-setter">
                                        <div className="goal-inputs">
                                            <div className="input-group">
                                                <label>Target Monthly Passive Income ($)</label>
                                                <input type="number" placeholder="e.g. 5000" />
                                            </div>
                                            <div className="input-group">
                                                <label>Average Monthly Cash Flow Per Property ($)</label>
                                                <input type="number" placeholder="e.g. 800" />
                                            </div>
                                            <div className="input-group">
                                                <label>Target Time Horizon (Years)</label>
                                                <input type="number" placeholder="e.g. 5" />
                                            </div>
                                        </div>
                                        <div className="goal-results">
                                            <div className="result-item">
                                                <span className="result-label">Units Needed:</span> 6-7 properties
                                            </div>
                                            <div className="result-item">
                                                <span className="result-label">Units to Acquire:</span> 4-5 properties
                                            </div>
                                            <div className="result-item">
                                                <span className="result-label">Units Per Year:</span> ~1 property
                                            </div>
                                            <div className="result-item">
                                                <span className="result-label">Estimated Capital Required:</span> $200K-$250K
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </div>
                </section>

                <Footer />
            </div>
        </div>
    );
}